import {
  Badge,
  Box,
  Flex,
  HStack,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react";
import React from "react";

interface HubSpotContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  leadScore: number;
  lifecycleStage: string;
  leadStatus: string;
  hubspotOwner: string;
}

interface HubSpotDeal {
  id: string;
  name: string;
  stage: string;
  amount: number;
  closeDate: string;
  probability: number;
  source: string;
  owner: string;
}

interface HubSpotActivity {
  id: string;
  type: string;
  subject: string;
  timestamp: string;
  status: string;
  owner: string;
  direction?: string;
  duration?: string;
  notes?: string;
}

interface HubSpotTabProps {
  contact: HubSpotContact;
  deals: HubSpotDeal[];
  activities: HubSpotActivity[];
  properties: Record<string, string>;
}

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    full: date.toLocaleString()
  };
};

const getActivityStyle = (type: string) => {
  const styles: Record<string, { icon: string; color: string }> = {
    email: { icon: "📧", color: "blue" },
    call: { icon: "📞", color: "green" },
    meeting: { icon: "🤝", color: "purple" },
    note: { icon: "📝", color: "orange" }
  };
  return styles[type] || { icon: "📋", color: "gray" };
};

export const HubSpotTab: React.FC<HubSpotTabProps> = ({
  contact,
  deals,
  activities,
  properties
}) => {
  return (
    <VStack align="stretch" gap={6}>
      {/* Contact Overview */}
      <Box
        bg="orange.50"
        border="2px"
        borderColor="orange.200"
        rounded="xl"
        p={6}
      >
        <HStack gap={4} mb={4}>
          <Box fontSize="2xl">🧑‍💼</Box>
          <Box>
            <Text fontSize="xl" fontWeight="bold" color="orange.800">
              Contact Information
            </Text>
            <Text fontSize="sm" color="orange.600">
              HubSpot Contact ID: {contact.id}
            </Text>
          </Box>
        </HStack>

        <SimpleGrid columns={2} gap={6}>
          <VStack align="start" gap={3}>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Name
              </Text>
              <Text fontSize="lg" fontWeight="bold">
                {contact.firstName} {contact.lastName}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Email
              </Text>
              <Text color="blue.600">{contact.email}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Phone
              </Text>
              <Text>{contact.phone}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Company
              </Text>
              <Text>{contact.company}</Text>
            </Box>
          </VStack>

          <VStack align="start" gap={3}>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Lead Score
              </Text>
              <HStack gap={2}>
                <Badge colorScheme="green" size="lg">
                  {contact.leadScore}
                </Badge>
                <Text fontSize="sm" color="gray.600">
                  / 100
                </Text>
              </HStack>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Lifecycle Stage
              </Text>
              <Badge colorScheme="purple" textTransform="capitalize">
                {contact.lifecycleStage}
              </Badge>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Lead Status
              </Text>
              <Badge colorScheme="blue" textTransform="capitalize">
                {contact.leadStatus}
              </Badge>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Owner
              </Text>
              <Text>{contact.hubspotOwner}</Text>
            </Box>
          </VStack>
        </SimpleGrid>
      </Box>

      {/* Deals Section */}
      <Box
        bg="green.50"
        border="2px"
        borderColor="green.200"
        rounded="xl"
        p={6}
      >
        <HStack gap={4} mb={4}>
          <Box fontSize="2xl">💰</Box>
          <Box>
            <Text fontSize="xl" fontWeight="bold" color="green.800">
              Active Deals
            </Text>
            <Text fontSize="sm" color="green.600">
              Revenue opportunities in pipeline
            </Text>
          </Box>
        </HStack>

        {deals.map((deal) => (
          <Box
            key={deal.id}
            bg="white"
            p={4}
            rounded="lg"
            border="1px"
            borderColor="green.300"
          >
            <Flex justify="space-between" align="start" mb={3}>
              <Box>
                <Text fontSize="lg" fontWeight="bold">
                  {deal.name}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Stage:{" "}
                  {deal.stage
                    .replace(/-/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </Text>
              </Box>
              <VStack align="end" gap={1}>
                <Text fontSize="xl" fontWeight="bold" color="green.600">
                  ${deal.amount.toLocaleString()}
                </Text>
                <Badge colorScheme="green">{deal.probability}% likely</Badge>
              </VStack>
            </Flex>

            <SimpleGrid columns={3} gap={4}>
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  Close Date
                </Text>
                <Text fontSize="sm">
                  {new Date(deal.closeDate).toLocaleDateString()}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  Source
                </Text>
                <Text fontSize="sm">{deal.source}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  Owner
                </Text>
                <Text fontSize="sm">{deal.owner}</Text>
              </Box>
            </SimpleGrid>
          </Box>
        ))}
      </Box>

      {/* Recent Activities */}
      <Box
        bg="blue.50"
        border="2px"
        borderColor="blue.200"
        rounded="xl"
        p={6}
      >
        <HStack gap={4} mb={4}>
          <Box fontSize="2xl">📋</Box>
          <Box>
            <Text fontSize="xl" fontWeight="bold" color="blue.800">
              Recent Activities
            </Text>
            <Text fontSize="sm" color="blue.600">
              Latest interactions and touchpoints
            </Text>
          </Box>
        </HStack>

        <VStack align="stretch" gap={3}>
          {activities.map((activity) => {
            const activityStyle = getActivityStyle(activity.type);

            return (
              <Box
                key={activity.id}
                bg="white"
                p={4}
                rounded="lg"
                border="1px"
                borderColor="blue.300"
              >
                <Flex justify="space-between" align="start" mb={2}>
                  <HStack gap={3}>
                    <Box fontSize="lg">{activityStyle.icon}</Box>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm">
                        {activity.subject}
                      </Text>
                      <HStack gap={2}>
                        <Badge colorScheme={activityStyle.color} size="sm">
                          {activity.type.toUpperCase()}
                        </Badge>
                        {activity.direction && (
                          <Badge variant="outline" size="sm">
                            {activity.direction}
                          </Badge>
                        )}
                        <Badge
                          colorScheme={
                            activity.status === "completed" ? "green" : "blue"
                          }
                          size="sm"
                        >
                          {activity.status}
                        </Badge>
                      </HStack>
                    </Box>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    {formatTimestamp(activity.timestamp).full}
                  </Text>
                </Flex>

                {activity.duration && (
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    <strong>Duration:</strong> {activity.duration}
                  </Text>
                )}

                {activity.notes && (
                  <Box bg="gray.50" p={2} rounded="md" mt={2}>
                    <Text fontSize="sm" color="gray.700">
                      <strong>Notes:</strong> {activity.notes}
                    </Text>
                  </Box>
                )}

                <Text fontSize="xs" color="gray.500" mt={2}>
                  <strong>Owner:</strong> {activity.owner}
                </Text>
              </Box>
            );
          })}
        </VStack>
      </Box>

      {/* Customer Properties */}
      <Box
        bg="purple.50"
        border="2px"
        borderColor="purple.200"
        rounded="xl"
        p={6}
      >
        <HStack gap={4} mb={4}>
          <Box fontSize="2xl">🏠</Box>
          <Box>
            <Text fontSize="xl" fontWeight="bold" color="purple.800">
              Customer Properties
            </Text>
            <Text fontSize="sm" color="purple.600">
              Qualification and preference details
            </Text>
          </Box>
        </HStack>

        <SimpleGrid columns={2} gap={4}>
          {Object.entries(properties).map(([key, value]) => (
            <Box
              key={key}
              bg="white"
              p={3}
              rounded="lg"
              border="1px"
              borderColor="purple.300"
            >
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
                {key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </Text>
              <Text fontSize="sm" fontWeight="bold">
                {value}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    </VStack>
  );
};